import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFreeAgentCallback } from "@/hooks/useFreeAgent";

/**
 * The OAuth landing page. FreeAgent redirects here with `code` and `state`;
 * we POST those to the Sidequester callback endpoint, which exchanges them
 * for tokens. We then bounce the user to their company overview to sync.
 *
 * `state` carries the originating company_id in the form `company:<id>`.
 */
export default function FreeAgentCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const callback = useFreeAgentCallback();
  const ran = useRef(false);

  const code = params.get("code") ?? "";
  const state = params.get("state") ?? "";
  const errorParam = params.get("error");

  useEffect(() => {
    if (ran.current) return;
    if (!code) return;

    const m = state.match(/company:(\d+)/);
    const companyId = m ? Number(m[1]) : NaN;

    if (!companyId) {
      navigate("/dashboard/app/companies", { replace: true });
      return;
    }

    ran.current = true;

    // Must match exactly what was sent when starting the OAuth flow — derived
    // from the current origin in both spots so dev port + prod domain "just work".
    const redirectUri = `${window.location.origin}/auth/freeagent/callback`;

    callback
      .mutateAsync({ code, state, redirectUri, companyId })
      .then(() => {
        navigate(`/dashboard/app/companies/${companyId}`, { replace: true });
      })
      .catch(() => {
        navigate(`/dashboard/app/companies/${companyId}`, { replace: true });
      });
  }, [code, state, callback, navigate]);

  if (errorParam) {
    return (
      <div className="container max-w-md py-16 text-center">
        <h1 className="text-lg font-semibold">FreeAgent connection cancelled</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You can try again from your company page.
        </p>
      </div>
    );
  }

  return (
    <div className="container py-16 text-center text-muted-foreground">
      Connecting your FreeAgent account…
    </div>
  );
}
